import { z } from 'zod'

// At least one digit, one lowercase, one uppercase, length 6+.
// Looser than the original `rgxExtension.password` — no special-character
// requirement and lower minimum length.
export const PASSWORD_REGEX = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}$/

// Generic international phone format: optional +, separators, and digits.
// Empty string is treated as "not provided" — the schema factory below
// returns an optional variant that allows that.
export const PHONE_REGEX =
  /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/

interface CreatePasswordSchemaArgs {
  /** Error message used when the value is empty. */
  requiredMessage: string
  /** Error message used when the value fails the format check. */
  invalidMessage: string
}

/**
 * Zod schema for a required password field. Empty → requiredMessage; fails
 * the regex → invalidMessage. The format check enforces: ≥6 chars,
 * at least one digit, one lowercase, one uppercase.
 */
export const createPasswordSchema = ({
  requiredMessage,
  invalidMessage
}: CreatePasswordSchemaArgs) =>
  z.string().min(1, requiredMessage).regex(PASSWORD_REGEX, invalidMessage)

interface CreatePhoneSchemaArgs {
  /** Error message used when the value is a non-empty invalid number. */
  invalidMessage: string
  /** When true, an empty string is rejected with `requiredMessage`. */
  required?: boolean
  /** Required only when `required: true`. */
  requiredMessage?: string
}

/**
 * Zod schema for a phone field. Empty is allowed (unless `required` is set);
 * any non-empty value must match the international phone regex.
 */
export const createPhoneSchema = ({
  invalidMessage,
  required = false,
  requiredMessage
}: CreatePhoneSchemaArgs) => {
  const base = z.string()
  if (required) {
    return base.min(1, requiredMessage ?? invalidMessage).regex(PHONE_REGEX, invalidMessage)
  }
  return base.refine((v) => v === '' || PHONE_REGEX.test(v), invalidMessage)
}
